import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AnimatedIcon } from '../../components/icons';
import { useRequestStore } from '../../store/requestStore';
import { useAuthStore } from '../../store/authStore';
import { JobLead, SurveyorProfile, RequestState, ServiceRequest, SURVEY_CATEGORIES } from '../../types';
import { jobLeadService } from '../../services/jobLeadService';
import { requestService } from '../../services/requestService';
import { getSurveyorProfile, getSurveyorServiceTypes } from '../../utils/surveyorApproval';
import { WEB_STATUS_COLORS } from '../../utils/statusPresentation';
import {
  CurrentJobLocation,
  getCurrentLocationCity,
  getNearestZimbabweCity,
  isRequestInCurrentLocation,
} from '../../utils/jobLocation';

const STATUS_COLORS = WEB_STATUS_COLORS;

const getCompactStatusText = (status: string) => {
  switch (status) {
    case 'UNDER_REVIEW': return '🟡 Review';
    case 'ASSIGNED': return '🔵 Assigned';
    case 'NEGOTIATING': return '🟡 Negotiating';
    case 'CONFIRMED': return '🟢 Confirmed';
    case 'CLOSED': return '🟢 Closed';
    case 'REQUEST_SUBMITTED': return '🔵 Submitted';
    case 'APPROVED': return '🟢 Approved';
    case 'DISPUTED': return '🟠 Disputed';
    case 'REJECTED': return '🔴 Rejected';
    default: return status.replace(/_/g, ' ');
  }
};

export const SurveyorBrowseJobs: React.FC = () => {
  const { user } = useAuthStore();
  const { requests, isLoading, fetchRequests } = useRequestStore();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [currentLocation, setCurrentLocation] = useState<CurrentJobLocation | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [advertisedLeads, setAdvertisedLeads] = useState<JobLead[]>([]);
  const [advertisedLeadsLoading, setAdvertisedLeadsLoading] = useState(false);
  const [nearbyRequests, setNearbyRequests] = useState<ServiceRequest[]>([]);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const navigate = useNavigate();

  // Surveyor's registered service types from profile
  const profile = getSurveyorProfile(user) as SurveyorProfile | null;
  const myServiceTypes: string[] = getSurveyorServiceTypes(user);

  const detectCurrentLocation = () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setCurrentLocation(null);
      setLocationError('Location access is required to show jobs where you are now.');
      return;
    }

    setIsLocating(true);
    setLocationError(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coordinates = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        const city = getNearestZimbabweCity(coordinates)?.city;
        setCurrentLocation({ ...coordinates, city });
        setLocationError(city ? null : 'We could not match your current position to a supported Zimbabwe city or town.');
        setIsLocating(false);
      },
      (error) => {
        setCurrentLocation(null);
        setLocationError(error.message || 'Allow location access to see jobs in your current city.');
        setIsLocating(false);
      },
      { enableHighAccuracy: false, timeout: 12000, maximumAge: 300000 },
    );
  };

  useEffect(() => {
    fetchRequests({ status: RequestState.APPROVED });
    detectCurrentLocation();
  }, []);

  useEffect(() => {
    const loadNearbyData = async () => {
      if (!currentLocation) {
        setAdvertisedLeads([]);
        setNearbyRequests([]);
        return;
      }
      const { latitude, longitude } = currentLocation;

      setAdvertisedLeadsLoading(true);
      setNearbyLoading(true);
      try {
        const [leadsRes, requestsRes] = await Promise.all([
          jobLeadService.listNearby({ latitude, longitude, radius_km: 75 }),
          requestService.listNearby({ latitude, longitude, radius_km: 75 }),
        ]);
        setAdvertisedLeads(leadsRes.results ?? []);
        // Apply city-name fallback for jobs that have no stored GPS coords
        const filtered = (requestsRes.results ?? []).filter(r => {
          if (r.latitude && r.longitude) return true; // already radius-filtered by backend
          return isRequestInCurrentLocation(r, currentLocation).matches;
        });
        setNearbyRequests(filtered);
      } catch {
        setAdvertisedLeads([]);
        setNearbyRequests([]);
      } finally {
        setAdvertisedLeadsLoading(false);
        setNearbyLoading(false);
      }
    };
    loadNearbyData();
  }, [currentLocation]);

  // ── Suspension gate ──────────────────────────────────────
  if (profile?.is_suspended) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column' as const, alignItems: 'center', justifyContent: 'center', minHeight: '60vh', padding: 40, textAlign: 'center' as const }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>🔒</div>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--sc-error)', margin: '0 0 10px' }}>Access Restricted</h2>
        <p style={{ fontSize: 14, color: 'var(--sc-text-secondary)', maxWidth: 440, lineHeight: 1.6, margin: '0 0 12px' }}>
          Your account is currently suspended. Browsing and accepting new jobs is disabled.
        </p>
        {profile.suspension_reason && (
          <div style={{ background: 'var(--sc-error-bg)', borderRadius: 10, border: '1px solid var(--sc-error)', padding: '12px 20px', fontSize: 13, color: 'var(--sc-error-text)', fontStyle: 'italic', maxWidth: 400 }}>
            Reason: {profile.suspension_reason}
          </div>
        )}
      </div>
    );
  }

  // Find which categories the surveyor qualifies for
  const myCategories = SURVEY_CATEGORIES.filter((cat) =>
    cat.services.some((s) => myServiceTypes.includes(s)),
  );

  // Filter requests: GPS-filtered by backend, city-name fallback already applied
  const qualifiedRequests = nearbyRequests;
  const currentCity = getCurrentLocationCity(currentLocation);

  // Further filter by selected category
  const categoryFiltered =
    selectedCategory !== null
      ? qualifiedRequests.filter((r) => {
          const cat = SURVEY_CATEGORIES.find((c) => c.id === selectedCategory);
          return cat ? cat.services.includes(r.service_type) : true;
        })
      : qualifiedRequests;

  // Search filter
  const filtered = search.trim()
    ? categoryFiltered.filter(
        (r) =>
          r.service_type?.toLowerCase().includes(search.toLowerCase()) ||
          r.address?.toLowerCase().includes(search.toLowerCase()) ||
          r.description?.toLowerCase().includes(search.toLowerCase()),
      )
    : categoryFiltered;

  const advertisedCategoryFiltered =
    selectedCategory !== null
      ? advertisedLeads.filter((lead) => {
          const cat = SURVEY_CATEGORIES.find((c) => c.id === selectedCategory);
          return cat ? cat.services.includes(lead.service_type) : true;
        })
      : advertisedLeads;

  const filteredAdvertisedLeads = search.trim()
    ? advertisedCategoryFiltered.filter(
        (lead) =>
          lead.service_type?.toLowerCase().includes(search.toLowerCase()) ||
          lead.location_address?.toLowerCase().includes(search.toLowerCase()) ||
          lead.description?.toLowerCase().includes(search.toLowerCase()),
      )
    : advertisedCategoryFiltered;

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-ZW', { day: 'numeric', month: 'short' });

  const getCatColor = (serviceType: string) => {
    const cat = SURVEY_CATEGORIES.find((c) => c.services.includes(serviceType));
    return cat?.color ?? '#636E72';
  };

  const getCatIcon = (serviceType: string) => {
    const cat = SURVEY_CATEGORIES.find((c) => c.services.includes(serviceType));
    return cat?.icon ?? '📋';
  };

  const handleBid = async (leadId: string) => {
    try {
      const result = await jobLeadService.bid(leadId);
      navigate(`/surveyor/chat/${result.chat_room_id}`);
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to place bid on this advertised job.');
    }
  };

  return (
    <div>
      {/* ── Page Header ── */}
      <div style={s.header}>
        <div>
          <h1 style={s.pageTitle}>Browse Available Jobs</h1>
          <p style={s.pageSubtitle}>
            {currentLocation
              ? `Jobs available near ${currentCity || 'your current location'}.`
              : 'Turn on location access to see jobs where you are now.'}
          </p>
        </div>
        <div style={s.statsRow}>
          <div style={s.statPill}>
            <span style={s.statNum}>{qualifiedRequests.length}</span>
            <span style={s.statLabel}>Jobs</span>
          </div>
          <div style={s.statPill}>
            <span style={{ ...s.statNum, color: '#6366F1' }}>{myServiceTypes.length}</span>
            <span style={s.statLabel}>Specs</span>
          </div>
        </div>
      </div>

      {/* ── Specialties Banner ── */}
      {myServiceTypes.length > 0 && (
        <div style={s.specialtiesBanner}>
            <div style={s.specialtiesLeft}>
              <span style={{ fontSize: 16, marginRight: 8 }}>🎯</span>
              <strong>Specs:</strong>
            </div>
            <div style={s.chipRow}>
              {myServiceTypes.map((st) => {
                const color = getCatColor(st);
                return (
                  <span
                    key={st}
                    style={{ ...s.specialtyChip, background: color + '18', color, borderColor: color + '44' }}
                  >
                    {getCatIcon(st)} {st}
                  </span>
                );
              })}
            </div>
          </div>
      )}

      <div style={s.locationBanner}>
        <AnimatedIcon name="location" size={18} color="var(--sc-brand-primary)" />
        <span style={s.locationBannerText}>
          {isLocating
            ? 'Detecting your current location...'
            : locationError
              ? locationError
              : `Showing jobs near ${currentCity || 'your current city'}`}
        </span>
        {!isLocating && locationError && (
          <button style={s.retryBtn} onClick={detectCurrentLocation}>
            Retry
          </button>
        )}
      </div>

      {/* ── Category Filter Tabs ── */}
      <div style={s.filterRow}>
            <button
              style={{ ...s.filterTab, ...(selectedCategory === null ? s.filterTabActive : {}) }}
              onClick={() => setSelectedCategory(null)}
            >
              All ({qualifiedRequests.length})
            </button>
            {myCategories.map((cat) => {
              const count = qualifiedRequests.filter((r) => cat.services.includes(r.service_type)).length;
              return (
                <button
                  key={cat.id}
                  style={{
                    ...s.filterTab,
                    ...(selectedCategory === cat.id
                      ? { borderColor: cat.color, color: cat.color, background: cat.color + '18' }
                      : {}),
                  }}
                  onClick={() => setSelectedCategory(selectedCategory === cat.id ? null : cat.id)}
                >
                  {cat.icon} {cat.name.split(' ')[0]} ({count})
                </button>
              );
            })}
          </div>

          {/* ── Search ── */}
          <div style={s.searchWrap}>
            <span style={{ fontSize: 14, color: '#B2BEC3' }}>🔍</span>
            <input
              type="text"
              placeholder="Search by service, location or description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={s.searchInput}
            />
            {search && (
              <button style={s.clearBtn} onClick={() => setSearch('')}>✕</button>
            )}
          </div>

          {/* ── Jobs List ── */}
          {filteredAdvertisedLeads.length > 0 && (
            <div style={s.advertisedSection}>
              <div style={s.sectionTitleRow}>
                <AnimatedIcon name="folder" size={18} color="var(--sc-brand-primary)" />
                <strong>Advertised leads near you</strong>
              </div>
              <div style={s.jobsGrid}>
                {filteredAdvertisedLeads.map((lead) => {
                  const color = getCatColor(lead.service_type);
                  return (
                    <div key={lead.id} style={{ ...s.jobCard, borderLeft: `4px solid ${color}` }}>
                      <div style={s.jobCardTop}>
                        <div style={s.jobIcon}>{getCatIcon(lead.service_type)}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ ...s.jobType, color }}>{lead.service_type}</div>
                          <div style={s.jobAddress}>📍 {lead.location_address}</div>
                        </div>
                      </div>
                      <p style={s.jobDesc}>{lead.description}</p>
                      <div style={s.leadSafetyBox}>
                        Admin-managed referral. Client phone is hidden to prevent off-platform contact.
                      </div>
                      <div style={{ ...s.jobFooter, borderTop: 'none', marginTop: -4 }}>
                        <button style={{ ...s.viewBtn, background: color }} onClick={() => handleBid(lead.id)} className="sc-press">
                          Bid & Chat →
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {advertisedLeadsLoading && (
            <div style={s.inlineLoading}>Checking approved advertised leads nearby...</div>
          )}
          {isLoading && filtered.length === 0 ? (
            <div style={s.emptyBox}>
              <div style={s.spinner} />
              <p>Loading available jobs...</p>
            </div>
          ) : !currentLocation ? (
            <div style={s.emptyBox}>
              <AnimatedIcon name="location" size={48} color="#B2BEC3" />
              <h3 style={{ fontSize: 17, fontWeight: 700, margin: '12px 0 8px' }}>Location required</h3>
              <p style={{ fontSize: 13, color: '#636E72' }}>
                Turn on location access to see jobs available where you are now.
              </p>
              <button style={{ ...s.retryBtn, marginTop: 14 }} onClick={detectCurrentLocation}>
                Retry location
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div style={s.emptyBox}>
              <AnimatedIcon name="search" size={48} color="#B2BEC3" />
              <h3 style={{ fontSize: 17, fontWeight: 700, margin: '12px 0 8px' }}>No matching jobs right now</h3>
              <p style={{ fontSize: 13, color: '#636E72' }}>
                New requests in your current location and specialty areas will appear here as they are approved by admin.
              </p>
            </div>
          ) : (
            <div style={s.jobsGrid}>
              {filtered.map((req) => {
                const color = getCatColor(req.service_type);
                const icon = getCatIcon(req.service_type);
                const sc = STATUS_COLORS[req.status] || STATUS_COLORS.REQUEST_SUBMITTED;
                return (
                  <div key={req.id} style={{ ...s.jobCard, borderLeft: `4px solid ${color}` }} className="zs-card-hover">
                    <div style={s.jobCardTop}>
                      <div style={s.jobIcon}>{icon}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ ...s.jobType, color }}>{req.service_type}</div>
                        <div style={s.jobAddress}>📍 {req.address}</div>
                      </div>
                      <span style={{ ...s.statusBadge, background: sc.bg, color: sc.color, borderColor: sc.border }}>
                        {getCompactStatusText(req.status)}
                      </span>
                    </div>
                    <p style={s.jobDesc}>{req.description}</p>
                    <div style={s.jobMeta}>
                      <span style={s.jobMetaItem}>📅 Posted {formatDate(req.created_at)}</span>
                      {req.deadline && (
                        <span style={s.jobMetaItem}>⏰ Deadline {formatDate(req.deadline)}</span>
                      )}
                      <span style={s.jobMetaItem}>
                        👤 {req.client?.first_name} {req.client?.last_name}
                      </span>
                    </div>
                    <div style={s.jobFooter}>
                      <Link to={req.deal_id ? `/surveyor/deal/${req.deal_id}` : `/surveyor/request/${req.id}`} style={{ ...s.viewBtn, background: color }}>
                        View Details →
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 22,
    flexWrap: 'wrap' as const,
    gap: 16,
  },
  pageTitle: { fontSize: 26, fontWeight: 800, color: 'var(--sc-text-primary)', margin: '0 0 4px', fontFamily: "'Outfit', sans-serif" },
  pageSubtitle: { fontSize: 14, color: 'var(--sc-text-secondary)', margin: 0 },

  statsRow: { display: 'flex', gap: 12 },
  statPill: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    background: 'var(--sc-bg-secondary)',
    borderRadius: 12,
    padding: '10px 20px',
    border: '1px solid var(--sc-border)',
    boxShadow: 'var(--sc-shadow-sm)',
    minWidth: 90,
  },
  statNum: { fontSize: 22, fontWeight: 800, color: 'var(--sc-success)' },
  statLabel: { fontSize: 11, color: 'var(--sc-text-secondary)', fontWeight: 500 },

  specialtiesBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    background: 'var(--sc-success-bg, rgba(16, 185, 129, 0.08))',
    border: '1px solid var(--sc-success, #10B981)',
    borderRadius: 12,
    padding: '14px 18px',
    marginBottom: 18,
    flexWrap: 'wrap' as const,
  },
  locationBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: 'var(--sc-bg-secondary)',
    border: '1px solid var(--sc-border)',
    borderRadius: 12,
    padding: '12px 16px',
    marginBottom: 18,
    boxShadow: 'var(--sc-shadow-sm)',
  },
  locationBannerText: {
    flex: 1,
    fontSize: 13,
    color: 'var(--sc-text-secondary)',
  },
  retryBtn: {
    border: '1px solid var(--sc-brand-primary)',
    background: 'var(--sc-brand-primary-bg)',
    color: 'var(--sc-brand-primary)',
    borderRadius: 8,
    padding: '7px 12px',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  specialtiesLeft: {
    display: 'flex',
    alignItems: 'center',
    color: 'var(--sc-text-primary)',
    fontSize: 13,
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
    paddingTop: 2,
  },
  chipRow: { display: 'flex', flexWrap: 'wrap' as const, gap: 8 },
  specialtyChip: {
    padding: '4px 10px',
    borderRadius: 8,
    border: '1px solid',
    fontSize: 12,
    fontWeight: 600,
  },

  filterRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 16,
    overflowX: 'auto',
    paddingBottom: 6,
    width: '100%',
    WebkitOverflowScrolling: 'touch',
  },
  filterTab: {
    padding: '8px 16px',
    borderRadius: 20,
    border: '1px solid var(--sc-border)',
    background: 'var(--sc-bg-secondary)',
    color: 'var(--sc-text-secondary)',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap' as const,
    flexShrink: 0,
  },
  filterTabActive: { background: 'var(--sc-success-bg)', borderColor: 'var(--sc-success)', color: 'var(--sc-success)' },

  searchWrap: {
    display: 'flex',
    alignItems: 'center',
    background: 'var(--sc-bg-secondary)',
    borderRadius: 12,
    padding: '10px 16px',
    gap: 10,
    border: '1px solid var(--sc-border)',
    boxShadow: 'var(--sc-shadow-sm)',
    marginBottom: 20,
  },
  searchInput: {
    flex: 1,
    border: 'none',
    background: 'transparent',
    outline: 'none',
    fontSize: 14,
    color: 'var(--sc-text-primary)',
    fontFamily: 'inherit',
  },
  clearBtn: {
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: 13,
    color: 'var(--sc-text-secondary)',
    fontFamily: 'inherit',
  },

  advertisedSection: { marginBottom: 20 },
  sectionTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
    color: 'var(--sc-text-primary)',
    fontSize: 15,
  },
  leadSafetyBox: {
    borderTop: '1px solid var(--sc-border)',
    paddingTop: 10,
    color: 'var(--sc-warning-text)',
    fontSize: 12,
    fontWeight: 700,
  },
  inlineLoading: {
    color: 'var(--sc-text-secondary)',
    fontSize: 12,
    marginBottom: 14,
  },

  jobsGrid: { display: 'flex', flexDirection: 'column' as const, gap: 10 },
  jobCard: {
    background: 'var(--sc-bg-secondary)',
    borderRadius: 14,
    padding: '12px 16px',
    border: '1px solid var(--sc-border)',
    boxShadow: 'var(--sc-shadow-sm)',
  },
  jobCardTop: { display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 10 },
  jobIcon: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    background: 'var(--sc-bg-primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 22,
    flexShrink: 0,
  },
  jobType: { fontSize: 14, fontWeight: 700, marginBottom: 2 },
  jobAddress: { fontSize: 12, color: 'var(--sc-text-secondary)' },
  statusBadge: { padding: '4px 10px', borderRadius: 7, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' as const, border: '1px solid' },

  jobDesc: { fontSize: 13, color: 'var(--sc-text-primary)', lineHeight: 1.35, margin: '0 0 8px' },
  jobMeta: { display: 'flex', flexWrap: 'wrap' as const, gap: 12, marginBottom: 14 },
  jobMetaItem: { fontSize: 12, color: 'var(--sc-text-secondary)' },
  jobFooter: { borderTop: '1px solid var(--sc-border)', paddingTop: 12 },
  viewBtn: {
    display: 'inline-flex',
    padding: '8px 16px',
    borderRadius: 9,
    color: '#FFF',
    fontSize: 13,
    fontWeight: 600,
    textDecoration: 'none',
  },

  emptyBox: {
    textAlign: 'center' as const,
    padding: 60,
    background: 'var(--sc-bg-secondary)',
    borderRadius: 14,
    border: '1px solid var(--sc-border)',
    color: 'var(--sc-text-secondary)',
    boxShadow: 'var(--sc-shadow-sm)',
  },
  spinner: {
    width: 32,
    height: 32,
    border: '3px solid var(--sc-border)',
    borderTopColor: 'var(--sc-brand-primary)',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto 12px',
  },
};
